import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  Chip,
  FormControlLabel,
  Switch,
  MenuItem,
  Select,
  FormControl,
  InputLabel
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import api from '../../utils/api';
import useThemeStyles from '../../theme/useThemeStyles';

function LegalDocuments() {
  const styles = useThemeStyles();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [editingDocument, setEditingDocument] = useState(null);
  const [formData, setFormData] = useState({
    fileName: '',
    title: '',
    buttonText: '',
    content: '',
    isActive: true,
    sortOrder: 0,
    userTypes: ['both'],
    version: '1.0'
  });
  const [quillKey, setQuillKey] = useState(0); // Key to force ReactQuill re-render

  useEffect(() => {
    loadDocuments();
  }, []);

  const loadDocuments = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/admin/legal-documents');
      setDocuments(response.data);
    } catch (err) {
      setError('Failed to load legal documents');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingDocument(null);
    // Reset Quill key for new document
    setQuillKey(prev => prev + 1);
    setFormData({
      fileName: '',
      title: '',
      buttonText: '',
      content: '',
      isActive: true,
      sortOrder: documents.length,
      userTypes: ['both'],
      version: '1.0'
    });
    setOpenDialog(true);
  };

  // Helper function to decode HTML entities
  const decodeHtmlEntities = (html) => {
    if (typeof window === 'undefined' || !html) return html;
    
    const textarea = window.document.createElement('textarea');
    textarea.innerHTML = html;
    return textarea.value;
  };

  const handleEdit = (doc) => {
    setEditingDocument(doc);
    // Increment key to force ReactQuill to reinitialize with fresh content
    setQuillKey(prev => prev + 1);

    // Decode HTML entities if content is escaped (e.g., &lt; becomes <)
    let contentToSet = doc.content || '';
    if (contentToSet.includes('&lt;') || contentToSet.includes('&gt;') || contentToSet.includes('&amp;')) {
      try {
        contentToSet = decodeHtmlEntities(contentToSet);
      } catch (err) {
        // Fallback: manual entity replacement
        contentToSet = contentToSet
          .replace(/&lt;/g, '<')
          .replace(/&gt;/g, '>')
          .replace(/&amp;/g, '&')
          .replace(/&quot;/g, '"')
          .replace(/&#39;/g, "'");
      }
    }

    setFormData({
      fileName: doc.fileName,
      title: doc.title,
      buttonText: doc.buttonText,
      content: contentToSet, // ReactQuill expects raw HTML, not escaped
      isActive: doc.isActive,
      sortOrder: doc.sortOrder,
      userTypes: doc.userTypes,
      version: doc.version
    });
    setOpenDialog(true);
  };

  const handleDelete = async (documentId) => {
    if (!window.confirm('Are you sure you want to delete this legal document?')) {
      return;
    }

    try {
      await api.delete(`/api/admin/legal-documents/${documentId}`);
      setSuccess('Legal document deleted successfully');
      loadDocuments();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete legal document');
    }
  };

  const handleDialogClose = () => {
    setOpenDialog(false);
    setEditingDocument(null);
    // Reset form data after a small delay to ensure ReactQuill unmounts cleanly
    setTimeout(() => {
      setFormData({
        fileName: '',
        title: '',
        buttonText: '',
        content: '',
        isActive: true,
        sortOrder: 0,
        userTypes: ['both'],
        version: '1.0'
      });
    }, 100);
  };

  const handleSave = async () => {
    try {
      setError('');
      
      if (!formData.fileName || !formData.title || !formData.buttonText || !formData.content) {
        setError('Please fill in all required fields');
        return;
      }

      // Validate fileName format
      if (!/^[a-z0-9-_]+$/.test(formData.fileName)) {
        setError('File name can only contain lowercase letters, numbers, hyphens, and underscores');
        return;
      }

      // Prepare data - ensure userTypes is an array
      const submitData = {
        ...formData,
        userTypes: Array.isArray(formData.userTypes) ? formData.userTypes : [formData.userTypes].filter(Boolean),
        sortOrder: parseInt(formData.sortOrder) || 0
      };

      if (editingDocument) {
        await api.put(`/api/admin/legal-documents/${editingDocument._id}`, submitData);
        setSuccess('Legal document updated successfully');
      } else {
        await api.post('/api/admin/legal-documents', submitData);
        setSuccess('Legal document created successfully');
      }

      handleDialogClose();
      loadDocuments();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      const errorMessage = err.response?.data?.message || 
                           err.response?.data?.error || 
                           err.message || 
                           'Failed to save legal document';
      setError(errorMessage);
    }
  };

  const quillModules = {
    toolbar: [
      [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      [{ 'script': 'sub'}, { 'script': 'super' }],
      [{ 'indent': '-1'}, { 'indent': '+1' }],
      [{ 'color': [] }, { 'background': [] }],
      [{ 'align': [] }],
      ['link', 'image'],
      ['clean']
    ]
  };

  if (loading) {
    return (
      <Box sx={styles.patterns.loadingState}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={styles.container.page}>
      <Box sx={styles.layout.flexBetween}>
        <Typography component="h2" className="pagename" sx={styles.text.pagename}>
          Legal Documents
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleCreate}
          sx={styles.button?.primary}
        >
          Add Legal Document
        </Button>
      </Box>
      <Typography variant="body1" sx={styles.text.sectionDescription} gutterBottom>
        Manage legal documents that appear in modals throughout the application. Use the WYSIWYG editor to format content.
      </Typography>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}
      <TableContainer component={Paper} sx={{ mt: 3 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>File Name</TableCell>
              <TableCell>Title</TableCell>
              <TableCell>Button Text</TableCell>
              <TableCell>User Types</TableCell>
              <TableCell>Sort Order</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {documents.map((doc) => (
              <TableRow key={doc._id}>
                <TableCell>{doc.fileName}</TableCell>
                <TableCell>{doc.title}</TableCell>
                <TableCell>{doc.buttonText}</TableCell>
                <TableCell>
                  {doc.userTypes.map((type) => (
                    <Chip key={type} label={type} size="small" sx={{ mr: 0.5 }} />
                  ))}
                </TableCell>
                <TableCell>{doc.sortOrder}</TableCell>
                <TableCell>
                  <Chip
                    label={doc.isActive ? 'Active' : 'Inactive'}
                    color={doc.isActive ? 'success' : 'default'}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <IconButton
                    size="small"
                    onClick={() => handleEdit(doc)}
                    color="primary"
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => handleDelete(doc._id)}
                    color="error"
                  >
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {/* Create/Edit Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleDialogClose}
        maxWidth="lg"
        fullWidth
        TransitionProps={{
          onEntered: () => {
            // Ensure ReactQuill is ready when dialog opens
            if (editingDocument && formData.content) {}
          }
        }}
      >
        <DialogTitle>
          {editingDocument ? 'Edit Legal Document' : 'Create Legal Document'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
            <TextField
              label="File Name"
              value={formData.fileName}
              onChange={(e) => setFormData({ ...formData, fileName: e.target.value.toLowerCase().replace(/[^a-z0-9-_]/g, '') })}
              fullWidth
              required
              disabled={!!editingDocument}
              helperText="Lowercase letters, numbers, hyphens, underscores only. Use terms-conditions, privacy-policy, cookies-policy, business-terms for app links. Cannot be changed after creation."
            />
            <TextField
              label="Title"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              fullWidth
              required
            />
            <TextField
              label="Button Text"
              value={formData.buttonText}
              onChange={(e) => setFormData({ ...formData, buttonText: e.target.value })}
              fullWidth
              required
              helperText="Text displayed on the button/link that opens this document"
            />
            <FormControl fullWidth>
              <InputLabel>User Types</InputLabel>
              <Select
                multiple
                value={Array.isArray(formData.userTypes) ? formData.userTypes : [formData.userTypes].filter(Boolean)}
                onChange={(e) => {
                  const value = e.target.value;
                  setFormData({ ...formData, userTypes: typeof value === 'string' ? value.split(',') : value });
                }}
                renderValue={(selected) => {
                  if (Array.isArray(selected)) {
                    return selected.join(', ');
                  }
                  return selected || 'both';
                }}
              >
                <MenuItem value="user">User</MenuItem>
                <MenuItem value="supplier">Supplier</MenuItem>
                <MenuItem value="both">Both</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Sort Order"
              type="number"
              value={formData.sortOrder}
              onChange={(e) => setFormData({ ...formData, sortOrder: parseInt(e.target.value) || 0 })}
              fullWidth
            />
            <TextField
              label="Version"
              value={formData.version}
              onChange={(e) => setFormData({ ...formData, version: e.target.value })}
              fullWidth
            />
            <FormControlLabel
              control={
                <Switch
                  checked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                />
              }
              label="Active"
            />
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Content (WYSIWYG Editor)
              </Typography>
              <Box
                sx={{
                  '& .ql-toolbar': {
                    position: 'sticky',
                    top: 0,
                    zIndex: 10,
                    backgroundColor: 'background.paper',
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    padding: '8px',
                  },
                  '& .ql-container': {
                    minHeight: '300px',
                    maxHeight: '500px',
                    overflowY: 'auto',
                  },
                  '& .ql-editor': {
                    minHeight: '300px',
                  },
                }}
              >
                <ReactQuill
                  key={quillKey} // Force re-render when switching documents
                  theme="snow"
                  value={formData.content || ''}
                  onChange={(value) => {
                    // ReactQuill onChange returns HTML string
                    setFormData({ ...formData, content: value });
                  }}
                  modules={quillModules}
                  style={{ marginBottom: '50px' }}
                />
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" color="primary">
            {editingDocument ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default LegalDocuments;
